import {
  Button,
  Checkbox,
  Group,
  ScrollArea,
  Stack,
  Table,
  Text,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import { captureMessage } from '@sentry/react';
import classNames from 'classnames/bind';
import { i18nKeys, useTranslation } from 'locales';
import { useRef, useState } from 'react';
import DraggableList from 'react-draggable-list';
import { FormProvider, useForm } from 'react-hook-form';
import { type FieldDescriptor, useLoadFieldCatalogue } from 'shared/hooks/use-load-field-catalogue';
import { type TableColumnAttributes, useUpdateViews } from 'shared/hooks/use-update-views';
import type { ResourceType } from 'types/resource-types';
import type { TableColumn, TableView } from 'types/views';

import { Icon, IconName } from '../Icon';

import styleIdentifiers from './ChooseColumnModal.module.scss';

const styles = classNames.bind(styleIdentifiers);

type Props = {
  tableName: string;
  currentView: TableView;
  callbackAction: Function;
  onSaved?: () => void;
};

type PickerColumn = Pick<TableColumn, 'name' | 'name_translated' | 'column_type'>;

type SelectedColumn = PickerColumn & Partial<Pick<TableColumn, 'id' | 'type'>>;

const toPickerColumn = (descriptor: FieldDescriptor): PickerColumn => ({
  name: descriptor.key,
  name_translated: descriptor.label,
  column_type: descriptor.type,
});

export default function ChooseColumnModal({
  tableName,
  currentView,
  callbackAction,
  onSaved,
}: Props) {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState<string>('');

  const { fieldCatalogue } = useLoadFieldCatalogue({ viewType: tableName });
  const columns = fieldCatalogue.filter(descriptor => descriptor.displayable).map(toPickerColumn);
  const { updateViews } = useUpdateViews();

  const form = useForm<{ table_columns: SelectedColumn[] }>({
    defaultValues: {
      table_columns: currentView.table_columns,
    },
  });

  const { watch, setValue } = form;

  const tableRef = useRef(null);

  const onMoveEnd = (value: any) => {
    setValue('table_columns', value);
  };

  const tableColumns = watch('table_columns');

  // The current view's selected columns are the single source of truth for the
  // checkbox state — derive it instead of mirroring it in a parallel array.
  const isColumnSelected = (column: PickerColumn) =>
    tableColumns.some(tableColumn => tableColumn.name === column.name);

  const save = () => {
    const tableColumnsAttributes: TableColumnAttributes[] = tableColumns.map(
      (tableColumn, index) => ({
        ...tableColumn,
        order: index + 1,
        id: tableColumn.type === 'table_column' ? tableColumn.id : undefined,
      }),
    );

    currentView.table_columns
      .filter(item => !tableColumns.some(column => column.name === item.name))
      .forEach(column => {
        tableColumnsAttributes.push({ ...column, _destroy: true });
      });

    updateViews(
      {
        views_attributes: [
          {
            id: currentView.id,
            view_type: currentView.view_type,
            table_columns_attributes: tableColumnsAttributes,
          },
        ],
        // tableName is the resource the view belongs to; used only for the
        // ['views', resourceType] cache key the view list reads from.
        resourceType: tableName as ResourceType,
      },
      {
        onSuccess: () => {
          // callbackAction is the consumer's React Query refetch; call it directly
          // instead of routing through the saga getView (which fed the now-unread
          // redux list).
          callbackAction();
          onSaved?.();
        },
      },
    );
  };

  const onCheckboxChange = (item: PickerColumn) => {
    if (isColumnSelected(item)) {
      setValue(
        'table_columns',
        tableColumns.filter(tableColumn => tableColumn.name !== item.name),
      );
    } else {
      setValue('table_columns', [
        ...tableColumns,
        { name: item.name, name_translated: item.name_translated, column_type: item.column_type },
      ]);
    }
  };

  const removeColumn = (item: SelectedColumn) => {
    const index = tableColumns.findIndex(tableColumn => tableColumn.name === item.name);
    if (index === -1) {
      captureMessage('Tried to delete a column that was not found in the list', {
        extra: { item, tableColumns },
      });
    }
    setValue(
      'table_columns',
      tableColumns.filter((_, columnIndex) => columnIndex !== index),
    );
  };

  const filteredColumns =
    searchText === ''
      ? columns
      : columns.filter(col => col.name_translated.toLowerCase().includes(searchText.toLowerCase()));

  return (
    <Group h="60vh" p="md" grow align="start">
      <Stack w="50%" h="100%" gap={0}>
        <TextInput
          pb="sm"
          placeholder={t(i18nKeys.SEARCH)}
          onChange={e => setSearchText(e.currentTarget.value)}
        />
        <FormProvider {...form}>
          <ScrollArea h="100%">
            <Table stickyHeader>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th />
                  <Table.Th>
                    <Text size="xs" fw="bold" c="gray.6">
                      {t(i18nKeys.COLUMN_NAME).toUpperCase()}
                    </Text>
                  </Table.Th>
                  <Table.Th>
                    <Text size="xs" fw="bold" c="gray.6">
                      {t(i18nKeys.TYPE_COLUMN).toUpperCase()}
                    </Text>
                  </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filteredColumns.map(column => {
                  return (
                    <Table.Tr
                      onClick={() => {
                        onCheckboxChange(column);
                      }}
                      key={column.name}
                    >
                      <Table.Td>
                        <Checkbox checked={isColumnSelected(column)} />
                      </Table.Td>
                      <Table.Td align="left">
                        <Text size="sm">{column.name_translated}</Text>
                      </Table.Td>
                      <Table.Td align="left">
                        <Text size="sm">
                          {t(`COLUMN_TYPE.${column.column_type.toUpperCase()}`)}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </FormProvider>
      </Stack>
      <div style={{ height: '100%' }} ref={tableRef}>
        <Group pb="sm" justify="end">
          <Button onClick={save}>{t(i18nKeys.SAVE)}</Button>
        </Group>
        <ScrollArea h="calc(100% - 50px)" pr="md">
          <DraggableList<SelectedColumn, { onClickDelete: (item: SelectedColumn) => void }, any>
            itemKey="name"
            template={DragItem as any}
            list={tableColumns}
            onMoveEnd={onMoveEnd}
            container={() => tableRef.current || document.body}
            commonProps={{ onClickDelete: removeColumn }}
          />
        </ScrollArea>
      </div>
    </Group>
  );
}

type DragProps = { item: any; dragHandleProps: any; list: any; commonProps: any };

const DragItem = ({ item, dragHandleProps, commonProps }: DragProps) => {
  const { t } = useTranslation();

  return (
    <div key={item.name} className={styles('draggable-column-item')} {...dragHandleProps}>
      <div className={styles('name-container')}>
        <Icon name={IconName.MOVE_VERTICAL} />

        <Text size="sm">{item.name_translated}</Text>
      </div>
      <div className={styles('field-container')}>
        <Text size="sm" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
          {/* @ts-ignore */}
          {t(i18nKeys.COLUMN_TYPE[item.column_type.toUpperCase()])}
        </Text>
        <UnstyledButton
          aria-label={t(i18nKeys.DELETE)}
          onClick={() => commonProps.onClickDelete(item)}
        >
          <Icon name={IconName.SIMPLE_REMOVE} />
        </UnstyledButton>
      </div>
    </div>
  );
};
